import React, { useState } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  TextField,
  Button,
  Typography,
  Alert,
  InputAdornment,
  IconButton,
  Link,
  CircularProgress,
  Container
} from '@mui/material';
import {
  Person,
  Lock,
  Visibility,
  VisibilityOff,
  Warning
} from '@mui/icons-material';
import { loginUser } from '../../redux/loginUser';

const ClientLogin = () => {
  const [userId, setUserId] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const dispatch = useDispatch();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');

    try {
      const result = await dispatch(loginUser(userId, password));
      if (result === true) {
        navigate('/manage-device');
      } else {
        setError('Invalid credentials. Please try again.');
        setUserId('');
        setPassword('');
      }
    } catch (err) {
      setError('Login failed. Please try again.');
      setUserId('');
      setPassword('');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        width: '100vw',
        position: 'fixed',
        top: 0,
        left: 0,
        background: 'linear-gradient(135deg, #1e3c72 0%, #2a5298 25%, #4a90e2 50%, #87ceeb 75%, #b8e6f5 100%)',
        backgroundSize: '400% 400%',
        animation: 'waterFlow 20s ease infinite',
        '@keyframes waterFlow': {
          '0%': { backgroundPosition: '0% 50%' },
          '25%': { backgroundPosition: '100% 25%' },
          '50%': { backgroundPosition: '50% 100%' },
          '75%': { backgroundPosition: '25% 0%' },
          '100%': { backgroundPosition: '0% 50%' }
        },
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 2,
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: `
            radial-gradient(circle at 10% 20%, rgba(135, 206, 235, 0.3) 0%, transparent 50%),
            radial-gradient(circle at 80% 10%, rgba(70, 130, 180, 0.2) 0%, transparent 50%),
            radial-gradient(circle at 40% 70%, rgba(173, 216, 230, 0.25) 0%, transparent 50%),
            radial-gradient(circle at 90% 90%, rgba(0, 191, 255, 0.15) 0%, transparent 50%)
          `,
          pointerEvents: 'none',
          animation: 'waterRipple 15s ease-in-out infinite',
        },
        '@keyframes waterRipple': {
          '0%, 100%': { opacity: 0.3 },
          '50%': { opacity: 0.6 }
        },
        '&::after': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: `
            linear-gradient(45deg, transparent 40%, rgba(255, 255, 255, 0.05) 50%, transparent 60%),
            linear-gradient(-45deg, transparent 40%, rgba(135, 206, 235, 0.1) 50%, transparent 60%)
          `,
          backgroundSize: '100px 100px, 150px 150px',
          animation: 'waterWave 25s linear infinite',
          pointerEvents: 'none'
        },
        '@keyframes waterWave': {
          '0%': { transform: 'translateX(-100px) translateY(-100px)' },
          '100%': { transform: 'translateX(100px) translateY(100px)' }
        }
      }}
    >
      <Container maxWidth="sm">
        <Card
          sx={{
            maxWidth: 450,
            mx: 'auto',
            background: 'rgba(255, 255, 255, 0.95)',
            backdropFilter: 'blur(20px)',
            borderRadius: 4,
            boxShadow: '0 20px 40px rgba(30, 60, 114, 0.2), 0 0 0 1px rgba(135, 206, 235, 0.3) inset',
            overflow: 'visible',
            transition: 'all 0.3s ease',
            border: '1px solid rgba(135, 206, 235, 0.2)',
            '&:hover': {
              transform: 'translateY(-5px)',
              boxShadow: '0 30px 60px rgba(30, 60, 114, 0.25), 0 0 0 1px rgba(135, 206, 235, 0.4) inset'
            }
          }}
        >
          <CardContent sx={{ p: 0 }}>
            {/* Header Section */}
            <Box
              sx={{
                textAlign: 'center',
                p: 5,
                pb: 3,
                background: 'linear-gradient(135deg, rgba(135, 206, 235, 0.1), rgba(173, 216, 230, 0.05))',
                position: 'relative',
                '&::before': {
                  content: '""',
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  background: 'linear-gradient(45deg, transparent 30%, rgba(135, 206, 235, 0.05) 50%, transparent 70%)',
                  animation: 'headerWave 8s ease-in-out infinite'
                },
                '@keyframes headerWave': {
                  '0%, 100%': { transform: 'translateX(-10px)' },
                  '50%': { transform: 'translateX(10px)' }
                }
              }}
            >
              {/* Logo */}
              <Box sx={{ mb: 4, zIndex: 1, position: 'relative' }}>
                <img
                  src="/images/logo.png"
                  alt="Alphatech Logo"
                  style={{
                    maxHeight: '80px',
                    maxWidth: '200px',
                    width: 'auto',
                    height: 'auto',
                    filter: 'drop-shadow(0 4px 8px rgba(30, 60, 114, 0.15))',
                    transition: 'transform 0.3s ease'
                  }}
                  onMouseOver={(e) => e.target.style.transform = 'scale(1.05)'}
                  onMouseOut={(e) => e.target.style.transform = 'scale(1)'}
                />
              </Box>

              <Typography
                variant="h4"
                sx={{
                  fontWeight: 700,
                  background: 'linear-gradient(135deg, #1e3c72, #2a5298, #4a90e2)',
                  backgroundClip: 'text',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  mb: 1,
                  letterSpacing: '-0.5px',
                  zIndex: 1,
                  position: 'relative'
                }}
              >
                Welcome Back
              </Typography>
              
              <Typography
                variant="body1"
                sx={{
                  color: '#1e3c72',
                  fontSize: '1.1rem',
                  fontWeight: 500,
                  zIndex: 1,
                  position: 'relative'
                }}
              >
                Sign in to your Alphatech account
              </Typography>
            </Box>

            {/* Form Section */}
            <Box sx={{ p: 5, pt: 3 }}>
              {error && (
                <Alert
                  severity="error"
                  icon={<Warning />}
                  sx={{
                    mb: 3,
                    borderRadius: 2,
                    background: 'linear-gradient(135deg, #ff6b6b, #ee5a52)',
                    color: 'white',
                    borderLeft: '4px solid #ff5252',
                    boxShadow: '0 4px 12px rgba(255, 107, 107, 0.3)',
                    '& .MuiAlert-icon': {
                      color: 'white'
                    }
                  }}
                >
                  {error}
                </Alert>
              )}

              <Box component="form" onSubmit={handleSubmit}>
                {/* User ID Field */}
                <TextField
                  fullWidth
                  label="User ID"
                  value={userId}
                  onChange={(e) => setUserId(e.target.value)}
                  placeholder="Enter your user ID"
                  required
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Person sx={{ color: '#2a5298' }} />
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    mb: 3,
                    '& .MuiOutlinedInput-root': {
                      height: '56px',
                      borderRadius: 2,
                      background: 'rgba(255, 255, 255, 0.9)',
                      backdropFilter: 'blur(10px)',
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        transform: 'translateY(-2px)',
                        boxShadow: '0 4px 12px rgba(42, 82, 152, 0.15)'
                      },
                      '&.Mui-focused': {
                        transform: 'translateY(-2px)',
                        background: 'rgba(255, 255, 255, 1)',
                        '& fieldset': {
                          borderColor: '#2a5298',
                          borderWidth: '2px'
                        }
                      }
                    },
                    '& .MuiInputLabel-root.Mui-focused': {
                      color: '#2a5298'
                    }
                  }}
                />

                {/* Password Field */}
                <TextField
                  fullWidth
                  label="Password"
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Enter your password"
                  required
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Lock sx={{ color: '#2a5298' }} />
                      </InputAdornment>
                    ),
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() => setShowPassword(!showPassword)}
                          edge="end"
                          sx={{ 
                            color: '#6c757d',
                            '&:hover': { color: '#2a5298' }
                          }}
                        >
                          {showPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    mb: 2,
                    '& .MuiOutlinedInput-root': {
                      height: '56px',
                      borderRadius: 2,
                      background: 'rgba(255, 255, 255, 0.9)',
                      backdropFilter: 'blur(10px)',
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        transform: 'translateY(-2px)',
                        boxShadow: '0 4px 12px rgba(42, 82, 152, 0.15)'
                      },
                      '&.Mui-focused': {
                        transform: 'translateY(-2px)',
                        background: 'rgba(255, 255, 255, 1)',
                        '& fieldset': {
                          borderColor: '#2a5298',
                          borderWidth: '2px'
                        }
                      }
                    },
                    '& .MuiInputLabel-root.Mui-focused': {
                      color: '#2a5298'
                    }
                  }}
                />

                {/* Forgot Password Link */}
                <Box sx={{ textAlign: 'right', mb: 4 }}>
                  <Link
                    href="/forgot-password"
                    sx={{
                      color: '#2a5298',
                      textDecoration: 'none',
                      fontSize: '0.95rem',
                      fontWeight: 500,
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        color: '#1e3c72',
                        textDecoration: 'underline'
                      }
                    }}
                  >
                    Forgot your password?
                  </Link>
                </Box>

                {/* Sign In Button */}
                <Button
                  type="submit"
                  fullWidth
                  disabled={isLoading}
                  sx={{
                    height: '56px',
                    background: 'linear-gradient(135deg, #1e3c72, #2a5298, #4a90e2)',
                    borderRadius: 2,
                    fontSize: '1.1rem',
                    fontWeight: 600,
                    letterSpacing: '0.5px',
                    textTransform: 'none',
                    color: '#ffffff', // Explicit white color
                    boxShadow: '0 8px 20px rgba(30, 60, 114, 0.4)',
                    transition: 'all 0.3s ease',
                    position: 'relative',
                    overflow: 'hidden',
                    '&:hover': {
                      transform: 'translateY(-2px)',
                      boxShadow: '0 12px 30px rgba(30, 60, 114, 0.5)',
                      background: 'linear-gradient(135deg, #2a5298, #4a90e2, #1e3c72)',
                      color: '#ffffff' // Ensure white on hover
                    },
                    '&:active': {
                      transform: 'translateY(0)'
                    },
                    '&:disabled': {
                      opacity: 0.7,
                      transform: 'none',
                      color: '#ffffff' // White even when disabled
                    },
                    '&::before': {
                      content: '""',
                      position: 'absolute',
                      top: 0,
                      left: '-100%',
                      width: '100%',
                      height: '100%',
                      background: 'linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.2), transparent)',
                      transition: 'left 0.5s'
                    },
                    '&:hover::before': {
                      left: '100%'
                    },
                    // Ensure text color is always white
                    '& .MuiButton-label': {
                      color: '#ffffff'
                    }
                  }}
                >
                  {isLoading ? (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, color: '#ffffff' }}>
                      <CircularProgress size={20} sx={{ color: '#ffffff' }} />
                      <Typography sx={{ color: '#ffffff', fontWeight: 600 }}>
                        Signing in...
                      </Typography>
                    </Box>
                  ) : (
                    <Typography sx={{ color: '#ffffff', fontWeight: 600, fontSize: '1.1rem' }}>
                      Sign In
                    </Typography>
                  )}
                </Button>
              </Box>
            </Box>
          </CardContent>
        </Card>
      </Container>
    </Box>
  );
};

export default ClientLogin;
